/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */

import type { LiveObject } from "@liveblocks/client"
import {
  type Diff,
  diff,
  type DiffArray,
  type DiffDeleted,
  type DiffEdit,
  type DiffNew,
} from "deep-diff"

import type { UpdateStorageObject } from "./CollaborationRoom"
import { wrapWithLiveblocksModel } from "./wrapWithLiveblocksModel"

interface HandleDocumentStorageChangesArgs {
  storage: LiveObject<Liveblocks["Storage"]>
  localData: UpdateStorageObject
}

export function handleDocumentStorageChanges(
  args: HandleDocumentStorageChangesArgs,
) {
  const { storage, localData } = args

  const diffs =
    diff(
      storage.get("document")?.toImmutable(),
      localData.document ?? storage.get("document")?.toImmutable(),
    ) ?? []

  diffs.sort(sortDiff).forEach((diff) => {
    /**
     * If the diff is an array change, we need to handle it differently
     * as we need to handle the index change of the array
     */
    if (isArrayKind(diff)) handleArrayDiff(diff, storage)

    /**
     * If the diff is a delete, we need to delete the item
     */
    if (isDeleteKind(diff)) handleDeleteDiff(diff, storage)

    /**
     * If the diff is an edit, we need to update the item
     */
    if (isUpdateKind(diff)) handleUpdateDiff(diff, storage)

    /**
     * If the diff is an add, we need to add the item
     */
    if (isAddKind(diff)) handleAddDiff(diff, storage)
  })
}

function isLastItem(diff: Diff<unknown, unknown>, index: number) {
  return index === (diff.path ?? []).length - 1
}

function sortDiff(a: Diff<unknown, unknown>, b: Diff<unknown, unknown>) {
  // Prioritize array deletions first
  const isADelete = a.kind === "A" && a.item.kind === "D"
  const isBDelete = b.kind === "A" && b.item.kind === "D"

  if (isADelete && !isBDelete) return -1
  if (!isADelete && isBDelete) return 1

  // Delete items we go backwards to the front 2,1,0.
  // This ensures we delete the last item first, if we delete the first item first we will shift the index of the second item.
  //
  // Edits and additions we go forwards to the back 0,1,2
  // This ensures we edit the first item first, if we edit the last item first we will shift the index of the second item.
  if (
    a.kind === "A" &&
    b.kind === "A" &&
    a.item.kind !== "D" &&
    b.item.kind !== "D"
  ) {
    if (a.index < b.index) return -1
    if (a.index > b.index) return 1
  }

  // If both are A/D or neither are A/D, sort by path length
  // We sort by path length to ensure we handle the last item first
  return (b.path?.length ?? 0) - (a.path?.length ?? 0)
}

function isDeleteKind(
  diff: Diff<unknown, unknown>,
): diff is DiffDeleted<unknown> {
  return diff.kind === "D"
}

function isAddKind(diff: Diff<unknown, unknown>): diff is DiffNew<unknown> {
  return diff.kind === "N"
}

function isUpdateKind(
  diff: Diff<unknown, unknown>,
): diff is DiffEdit<unknown, unknown> {
  return diff.kind === "E"
}

function isArrayKind(
  diff: Diff<unknown, unknown>,
): diff is DiffArray<unknown, unknown> {
  return diff.kind === "A"
}

function handleArrayDiff(
  diff: DiffArray<unknown, unknown>,
  storage: LiveObject<Liveblocks["Storage"]>,
) {
  // we use reduce to recursively traverse the storage object as we can't know
  // how many times we need to chain the get method
  diff.path?.reduce((acc, key, index) => {
    /**
     * If the diff is a delete, we need to remove the item
     */
    if (isLastItem(diff, index) && isDeleteKind(diff.item)) {
      acc.get(key)?.delete(diff.index)
      return acc
    }

    /**
     * If the diff is an add, we need to add the item
     * Depends on if the index already exists, we use either update
     * the existing index using set or insert a new one using insert
     */
    if (isLastItem(diff, index) && isAddKind(diff.item)) {
      const current = acc.get(key)
      const hasIndex = current?.get(diff.index) !== undefined

      hasIndex
        ? current?.set(diff.index, wrapWithLiveblocksModel(diff.item.rhs))
        : // set can not be used in this case as we are making a new addition
          // e.g. not updating an existing entry, insert will create a new entry
          current.insert(wrapWithLiveblocksModel(diff.item.rhs), diff.index)
      return acc
    }

    /**
     * If the diff is an edit, we need to update the item
     */
    if (isLastItem(diff, index) && isUpdateKind(diff.item)) {
      acc.get(key)?.set(diff.index, wrapWithLiveblocksModel(diff.item.rhs))
      return acc
    }

    return acc.get(key)
  }, storage.get("document"))
}

function handleDeleteDiff(
  diff: DiffDeleted<unknown>,
  storage: LiveObject<Liveblocks["Storage"]>,
) {
  // we use reduce to recursively traverse the storage object as we can't know
  // how many times we need to chain the get method
  diff.path?.reduce((acc, key, index) => {
    if (isLastItem(diff, index)) {
      acc.delete(key)
      return acc
    }

    return acc.get(key)
  }, storage.get("document"))
}

function handleUpdateDiff(
  diff: DiffEdit<unknown, unknown>,
  storage: LiveObject<Liveblocks["Storage"]>,
) {
  // we use reduce to recursively traverse the storage object as we can't know
  // how many times we need to chain the get method
  diff.path?.reduce((acc, key, index) => {
    if (isLastItem(diff, index)) {
      // Storage is serializable => "field: undefined" equals "delete field"
      // set(key, undefined) will not remove the field, use delete(key) instead
      if (diff.rhs === undefined) acc.delete(key)
      else acc.set(key, wrapWithLiveblocksModel(diff.rhs))
      return acc
    }

    return acc.get(key)
  }, storage.get("document"))
}

function handleAddDiff(
  diff: DiffNew<unknown>,
  storage: LiveObject<Liveblocks["Storage"]>,
) {
  // we use reduce to recursively traverse the storage object as we can't know
  // how many times we need to chain the get method
  diff.path?.reduce((acc, key, index) => {
    if (isLastItem(diff, index)) {
      acc.set(key, wrapWithLiveblocksModel(diff.rhs))
      return acc
    }

    return acc.get(key)
  }, storage.get("document"))
}
